/**
 * Age Pension entitlement — the means test, as a pure function.
 *
 * Given a client's already-assessed position (assessable assets, deemed and other assessable income,
 * relationship/homeowner/age status), this decides what pension they are entitled to: the assets test
 * and the income test, whichever is the more restrictive, plus Commonwealth Rent Assistance, the
 * illness-separated and one-of-a-couple-eligible adjustments, and the HEAS reduction. It also renders
 * the `socialSecurityBreakdown` — the line-by-line working the UI shows in its Age Pension tooltip.
 *
 * Extracted from `calculateFinancials`, which is where this ran inline (as its "--- 3. Age Pension
 * Calculation ---" block) and which still calls it: the pension is part of that function's cashflow,
 * so it cannot simply be lifted out to the server. What it CAN be is injected — see
 * {@link AgePensionAssessor}. `calculateFinancials` now asks an assessor for the entitlement rather
 * than computing it, and the projection engine passes in the one owned by its agePensionPlugin, which
 * is the single place both the entitlement and the fortnightly payment of it are described.
 *
 * This module does NOT decide what counts as an assessable asset or as assessable income — that is
 * upstream, in `assessableMeans.ts`, and is shared with the aged-care means test. It takes those totals
 * as given.
 */

import {
    PENSION_AGE,
    ANNUAL_REDUCTION_PER_1000_ASSETS,
    INCOME_REDUCTION_RATE,
    DEEMING_RATE_LOWER,
    DEEMING_RATE_UPPER,
    RENT_ASSISTANCE,
} from '../../../../../shared/src/calculation/constants/ratesAndThresholds.ts';
import { formatCurrency } from '../../../../../shared/src/calculation/support/currency.ts';
import type { FactFind } from '../../../../../shared/src/calculation/factFindTypes.ts';

/**
 * Commonwealth Rent Assistance (CRA) — annual amount. 75c per $1 of fortnightly rent above the situation's
 * minimum, capped at the maximum, annualised (×26). NOT assessable income for the pension test.
 */
export function computeRentAssistance(rentPerFortnight: number, situationKey: string, ra: typeof RENT_ASSISTANCE = RENT_ASSISTANCE): number {
    const band = (ra.rates as any)[situationKey] || ra.rates.single;
    const rent = rentPerFortnight || 0;
    const excess = Math.max(0, rent - band.minRent);
    const fortnightly = Math.min(excess * ra.taperRate, band.maxPayment);
    return fortnightly * 26;
}

/**
 * The assets-test thresholds for this client: `lower` is the level above which the pension starts to
 * taper, `upper` the level at which it reaches nil. Both depend on partnered/illness-separated status
 * and — the large one — on whether the client is a homeowner.
 */
export function resolveAssetTestLimits(
    rates: any,
    { isPartnered, isHomeowner, isIllnessSeparated }: { isPartnered: boolean; isHomeowner: boolean; isIllnessSeparated: boolean },
) {
    const group = isPartnered
        ? isIllnessSeparated
            ? rates.assetTestLimits?.coupleIllnessSeparated
            : rates.assetTestLimits?.couple
        : rates.assetTestLimits?.single;
    const branch = isHomeowner ? group?.homeowner : group?.nonHomeowner;
    return {
        lower: branch?.full ?? rates.assetTestLower[isPartnered ? 'couple' : 'single'],
        upper: branch?.cutoff ?? rates.assetTestUpper[isPartnered ? 'couple' : 'single'],
    };
}

/** The already-assessed position the means test is applied to. Every figure is annual unless named otherwise. */
export interface AgePensionInput {
    /** The rate tables for the year being assessed — current rates in year 1, indexed rates thereafter. */
    rates: any;

    // --- Status ---
    isPartnered: boolean;
    /** `'couple'` or `'single'` — the key the pension/income-test rate tables are indexed by. */
    relationshipStatus: string;
    /** A couple assessed apart (one in residential care, or separated by illness): each gets the single rate. */
    illnessSeparated: boolean;
    isHomeowner: boolean;
    clientAge: number | null;
    partnerAge: number | null;

    // --- Assets test ---
    assessableAssets: number;
    /** Gifts above the free areas, still counted for five years. Reported in the breakdown; already inside `assessableAssets`. */
    deprivedAsset: number;
    /** The former home's value where its two-year exemption after entering care has ended — reported in the breakdown; already inside `assessableAssets`. */
    formerHomeCounted?: number;

    // --- Income test ---
    deemedIncome: number;
    /** The balance the lower deeming rate applies up to — reported in the breakdown's deeming detail. */
    deemingThreshold: number;
    /** Combined financial assets the deeming above was struck on — reported for couples. */
    financialAssetsForDeeming: number;
    assessableIncomeFromPensions: number;
    netRentalIncomeForPension: number;
    otherAssessableIncome: number;
    investmentPropertyRentalForPension: number;
    partnerAssessableIncomeFromPensions: number;
    partnerNetRentalIncomeForPension: number;
    partnerOtherAssessableIncome: number;
    partnerInvestmentPropertyRentalForPension: number;

    // --- Rent Assistance ---
    clientResidency: string | undefined;
    partnerResidency: string | undefined;
    /** Retirement-village resident whose entry contribution was at or below the Extra Allowable Amount — a non-homeowner for CRA. */
    rvResident: boolean;
    rvIsHomeowner: boolean;
    /** Read for `rent.rentAmount`, `rent.landlordType` and `rent.sharedAccommodation` only. */
    factFind: FactFind;

    /** Reduction to the pension where the client draws the Home Equity Access Scheme. Currently always 0 — see calculateFinancials. */
    heasAgePensionReduction: number;

    // --- Breakdown detail only (no effect on the entitlement) ---
    structureAttribution: any;
    pre2015ABPDetails: any[];
    /** Each defined benefit pension's treatment (assessableMeans.ts DefinedBenefitDetail), for the breakdown. */
    definedBenefitDetails?: any[];
    /**
     * Strike the entitlement without writing out its working (`socialSecurityBreakdown` comes back empty).
     * For the projection's fortnightly re-assessment, which reads the figure and nothing else; the figure
     * is the same either way — the working is only formatted text built after it.
     */
    withoutBreakdown?: boolean;
}

/** What the means test decided. */
export interface AgePensionAssessment {
    /** The final annual entitlement, after every test and adjustment. 0 when not eligible. */
    agePension: number;
    /** Commonwealth Rent Assistance (annual), tapered jointly with the pension. Paid alongside it, reported separately. */
    rentAssistanceIncome: number;
    /**
     * The income the test was applied to — every assessable component summed. Computed regardless of
     * age eligibility, since `calculateFinancials` reports it for the aged-care means test too.
     */
    totalAssessableIncome: number;
    /** The line-by-line working, as the UI's Age Pension tooltip renders it. */
    socialSecurityBreakdown: any[];
}

/**
 * Assesses an Age Pension entitlement. Injected into `calculateFinancials` so a caller can own the
 * rules — see {@link calculateAgePension}, the default, and the projection engine's agePensionPlugin.
 */
export type AgePensionAssessor = (input: AgePensionInput) => AgePensionAssessment;

/**
 * The Age Pension means test: the lower of the assets test and the income test, plus Rent Assistance.
 *
 * A client (or their partner) must be of pension age at all; below it the entitlement is nil and the
 * breakdown says only that. Above it both tests run against the same maximum rate and the more
 * restrictive one wins.
 */
export const calculateAgePension: AgePensionAssessor = (input: AgePensionInput): AgePensionAssessment => {
    const {
        rates,
        isPartnered,
        relationshipStatus,
        illnessSeparated,
        isHomeowner,
        clientAge,
        partnerAge,
        assessableAssets,
        deprivedAsset,
        formerHomeCounted = 0,
        deemedIncome,
        deemingThreshold,
        financialAssetsForDeeming,
        assessableIncomeFromPensions,
        netRentalIncomeForPension,
        otherAssessableIncome,
        investmentPropertyRentalForPension,
        partnerAssessableIncomeFromPensions,
        partnerNetRentalIncomeForPension,
        partnerOtherAssessableIncome,
        partnerInvestmentPropertyRentalForPension,
        clientResidency,
        partnerResidency,
        rvResident,
        rvIsHomeowner,
        factFind,
        heasAgePensionReduction,
        structureAttribution,
        pre2015ABPDetails,
        definedBenefitDetails = [],
        withoutBreakdown = false,
    } = input;

    // Computed unconditionally — `calculateFinancials` reports it whether or not the client is of
    // pension age, because the aged-care means test reads it too.
    const totalAssessableIncome =
        deemedIncome +
        assessableIncomeFromPensions +
        netRentalIncomeForPension +
        otherAssessableIncome +
        investmentPropertyRentalForPension +
        partnerInvestmentPropertyRentalForPension +
        partnerAssessableIncomeFromPensions +
        partnerNetRentalIncomeForPension +
        partnerOtherAssessableIncome;

    let agePension = 0;
    let rentAssistanceIncome = 0;
    const basePensionRate = (rates.pensionRates as any)[relationshipStatus];
    const pensionRate = isPartnered && illnessSeparated ? rates.pensionRates.single * 2 : basePensionRate;
    const clientEligibleByAge = !!(clientAge && clientAge >= PENSION_AGE);
    const partnerEligibleByAge = !!(isPartnered && partnerAge && partnerAge >= PENSION_AGE);
    const isEligibleByAge = clientEligibleByAge || partnerEligibleByAge;
    const onlyOneOfCoupleEligible = isPartnered && clientEligibleByAge !== partnerEligibleByAge;

    let pensionFromAssetTest = pensionRate;
    let pensionFromIncomeTest = pensionRate;
    let pensionDeterminingTest: string;
    let socialSecurityBreakdown: any[];

    if (isEligibleByAge) {
        const { lower: assetLowerLimit, upper: assetUpperLimit } = resolveAssetTestLimits(rates, {
            isPartnered,
            isHomeowner,
            isIllnessSeparated: illnessSeparated,
        });
        let assetTestResult = 'Full Pension';
        let assetExcess = 0;
        let assetReductionApplied = 0;

        if (assessableAssets > assetUpperLimit) {
            pensionFromAssetTest = 0;
            assetTestResult = 'Nil';
        } else if (assessableAssets > assetLowerLimit) {
            assetExcess = assessableAssets - assetLowerLimit;
            const reduction = (assetExcess / 1000) * ANNUAL_REDUCTION_PER_1000_ASSETS;
            assetReductionApplied = reduction;
            pensionFromAssetTest = Math.max(0, pensionRate - reduction);
            assetTestResult = 'Part Pension';
        }

        const incomeLowerLimit = (rates.incomeTestLower as any)[relationshipStatus];
        const incomeUpperLimit = isPartnered
            ? illnessSeparated
                ? rates.incomeTestCutoff.coupleIllnessSeparated
                : rates.incomeTestCutoff.couple
            : rates.incomeTestCutoff.single;
        let incomeTestResult = 'Full Pension';

        if (totalAssessableIncome > incomeLowerLimit) {
            const excessIncome = totalAssessableIncome - incomeLowerLimit;
            const reduction = excessIncome * INCOME_REDUCTION_RATE;
            pensionFromIncomeTest = Math.max(0, pensionRate - reduction);
            incomeTestResult = 'Part Pension';
            if (pensionFromIncomeTest === 0) incomeTestResult = 'Nil';
        }

        agePension = Math.min(pensionFromAssetTest, pensionFromIncomeTest);
        pensionDeterminingTest = pensionFromAssetTest < pensionFromIncomeTest ? 'Asset Test' : 'Income Test';

        // --- Rent Assistance (CRA), integrated into the means-test taper (LA-2 taper fix) ---
        {
            const clientRents = clientResidency === 'renting';
            const partnerRents = isPartnered && partnerResidency === 'renting';
            const rvNonHomeownerRA = rvResident && !rvIsHomeowner;
            const landlordType = factFind.rent?.landlordType;
            const raLandlordEligible = landlordType !== 'family' && landlordType !== 'public_housing';
            /*
             * A village resident below the extra allowable amount is a non-homeowner whose ongoing
             * fees count as rent (SSA s 13(8)). The profile records those fees on the village
             * record; until 7 Oct 2026 only a MODELLED village move copied them into `rent`, so an
             * existing resident was paid no Rent Assistance at all.
             */
            const rentPerWeek = (factFind.rent?.rentAmount || 0) || (rvNonHomeownerRA ? Number(factFind.retirementVillage?.ongoingFees) || 0 : 0);
            const rentPerFortnight = rentPerWeek * 2;
            if ((clientRents || partnerRents || rvNonHomeownerRA) && raLandlordEligible && rentPerWeek > 0) {
                const raSituation = isPartnered
                    ? illnessSeparated
                        ? 'illnessSeparated'
                        : 'coupleCombined'
                    : factFind.rent?.sharedAccommodation === true
                      ? 'singleSharer'
                      : 'single';
                const raMax = computeRentAssistance(rentPerFortnight, raSituation);
                const rawAssetReduction = assessableAssets > assetLowerLimit ? ((assessableAssets - assetLowerLimit) / 1000) * ANNUAL_REDUCTION_PER_1000_ASSETS : 0;
                const rawIncomeReduction = totalAssessableIncome > incomeLowerLimit ? (totalAssessableIncome - incomeLowerLimit) * INCOME_REDUCTION_RATE : 0;
                const combinedMax = pensionRate + raMax;
                const combinedEntitlement = Math.min(Math.max(0, combinedMax - rawAssetReduction), Math.max(0, combinedMax - rawIncomeReduction));
                rentAssistanceIncome = Math.min(Math.max(0, combinedEntitlement - agePension), raMax);
            }
        }

        if (onlyOneOfCoupleEligible) {
            agePension = agePension / 2;
        }

        if (heasAgePensionReduction > 0) {
            agePension = Math.max(0, agePension - heasAgePensionReduction);
        }

        socialSecurityBreakdown = withoutBreakdown ? [] : [
            ...(isPartnered ? [{ label: 'Couple treatment', value: illnessSeparated ? 'Illness separated' : 'Standard couple' }] : []),
            { label: '--- Asset Test ---', value: '' },
            { label: 'Your Assets', value: formatCurrency(assessableAssets) },
            ...(formerHomeCounted > 0 ? [{ label: 'Former home counted (two-year exemption ended; assessed as a non-homeowner)', value: formatCurrency(formerHomeCounted) }] : []),
            { label: 'Deprived Assets', value: formatCurrency(deprivedAsset) },
            { label: 'Full Pension Limit', value: formatCurrency(assetLowerLimit) },
            { label: 'Nil Pension Limit', value: formatCurrency(assetUpperLimit) },
            { label: 'Result', value: assetTestResult },
            ...(assetTestResult === 'Part Pension'
                ? [
                      { label: 'Assets above full-pension limit', value: formatCurrency(assetExcess) },
                      { label: 'Asset-test taper rate', value: '$3 per fortnight per $1000' },
                      { label: 'Asset-test reduction applied', value: formatCurrency(assetReductionApplied) },
                  ]
                : []),
            { label: 'Pension under this test', value: formatCurrency(pensionFromAssetTest) },
            { label: '--- Income Test ---', value: '' },
            { label: 'Deemed Income', value: formatCurrency(deemedIncome) },
            ...(isPartnered ? [{ label: '  - Combined Financial Assets', value: formatCurrency(financialAssetsForDeeming) }] : []),
            {
                label: 'Other Assessable Income',
                value: formatCurrency(
                    assessableIncomeFromPensions +
                        netRentalIncomeForPension +
                        otherAssessableIncome +
                        investmentPropertyRentalForPension +
                        partnerInvestmentPropertyRentalForPension +
                        partnerAssessableIncomeFromPensions +
                        partnerNetRentalIncomeForPension +
                        partnerOtherAssessableIncome,
                ),
            },
            ...(assessableIncomeFromPensions > 0 ? [{ label: '  - Client Pension Income', value: formatCurrency(assessableIncomeFromPensions) }] : []),
            ...(netRentalIncomeForPension > 0 ? [{ label: '  - Client Rental Income (family home)', value: formatCurrency(netRentalIncomeForPension) }] : []),
            ...(investmentPropertyRentalForPension > 0
                ? [{ label: '  - Client Investment Property Rental', value: formatCurrency(investmentPropertyRentalForPension) }]
                : []),
            ...(otherAssessableIncome > 0 ? [{ label: '  - Client Other Income', value: formatCurrency(otherAssessableIncome) }] : []),
            ...(isPartnered && partnerAssessableIncomeFromPensions > 0
                ? [{ label: '  - Partner Pension Income', value: formatCurrency(partnerAssessableIncomeFromPensions) }]
                : []),
            ...(isPartnered && partnerNetRentalIncomeForPension > 0
                ? [{ label: '  - Partner Rental Income (family home)', value: formatCurrency(partnerNetRentalIncomeForPension) }]
                : []),
            ...(isPartnered && partnerInvestmentPropertyRentalForPension > 0
                ? [{ label: '  - Partner Investment Property Rental', value: formatCurrency(partnerInvestmentPropertyRentalForPension) }]
                : []),
            ...(isPartnered && partnerOtherAssessableIncome > 0 ? [{ label: '  - Partner Other Income', value: formatCurrency(partnerOtherAssessableIncome) }] : []),
            ...(structureAttribution?.structureLines?.length
                ? [
                      { label: '--- Trusts & companies (apportioned) ---', value: '' },
                      ...structureAttribution.structureLines.flatMap((line: any) => [
                          ...(line.clientAmount > 0 ? [{ label: `${line.label} (client share)`, value: formatCurrency(line.clientAmount) }] : []),
                          ...(isPartnered && line.partnerAmount > 0 ? [{ label: `${line.label} (partner share)`, value: formatCurrency(line.partnerAmount) }] : []),
                      ]),
                  ]
                : []),
            ...(pre2015ABPDetails.length > 0
                ? [
                      { label: '--- Pre-2015 ABP (deductible amount) ---', value: '' },
                      ...pre2015ABPDetails.flatMap((d) => [
                          ...(d.commutations > 0 ? [{ label: `  ${d.description}: Commutations (e.g. RAD drawdown)`, value: formatCurrency(d.commutations) }] : []),
                          {
                              label: `  ${d.description}: Relevant number (at commencement)`,
                              value: typeof d.relevantNumber === 'number' ? d.relevantNumber.toFixed(2) : String(d.relevantNumber),
                          },
                          { label: `  ${d.description}: Deductible amount (p.a.)`, value: formatCurrency(d.deductibleAmount) },
                          { label: `  ${d.description}: Assessable (payment − deductible)`, value: formatCurrency(d.assessableAmount) },
                      ]),
                  ]
                : []),
            ...(definedBenefitDetails.length > 0
                ? [
                      { label: '--- Defined benefit pension (payment less its tax-free part, capped at 10%) ---', value: '' },
                      ...definedBenefitDetails.flatMap((d: any) => [
                          { label: `  ${d.description}: Annual payment`, value: formatCurrency(d.annualPayment) },
                          { label: `  ${d.description}: Tax-free part counted${d.deductibleEntered > d.deductibleApplied + 0.5 ? ' (capped at 10%)' : ''}`, value: formatCurrency(d.deductibleApplied) },
                          { label: `  ${d.description}: Assessable income`, value: formatCurrency(d.assessableIncome) },
                          d.assetTestExempt
                              ? { label: `  ${d.description}: Assets test`, value: 'Not counted (exempt)' }
                              : { label: `  ${d.description}: Asset value (payment × factor ${d.valuationFactor ?? '—'})`, value: formatCurrency(d.assetValue) },
                      ]),
                  ]
                : []),
            { label: 'Total Assessable Income', value: formatCurrency(totalAssessableIncome) },
            { label: 'Full Pension Limit', value: formatCurrency(incomeLowerLimit) },
            { label: 'Nil Pension Limit', value: formatCurrency(incomeUpperLimit) },
            { label: 'Result', value: incomeTestResult },
            { label: 'Pension under this test', value: formatCurrency(pensionFromIncomeTest) },
            { label: '--- Deeming Details ---', value: '' },
            // Rounded to the rate's own precision: 0.0175 * 100 is 1.7500000000000002 in floating point,
            // and that is what a client saw in the breakdown (owner, 22 Sep 2026).
            { label: `Up to ${formatCurrency(deemingThreshold)}`, value: `${+(DEEMING_RATE_LOWER * 100).toFixed(4)}%` },
            { label: `Over ${formatCurrency(deemingThreshold)}`, value: `${+(DEEMING_RATE_UPPER * 100).toFixed(4)}%` },
            { label: '--- Final Entitlement ---', value: '' },
            { label: 'Determined By', value: pensionDeterminingTest },
            { label: 'Pension Before HEAS', value: formatCurrency(Math.min(pensionFromAssetTest, pensionFromIncomeTest)) },
            ...(heasAgePensionReduction > 0 ? [{ label: 'Less: HEAS Reduction', value: formatCurrency(-heasAgePensionReduction) }] : []),
            { label: 'Final Pension (p.a.)', value: formatCurrency(agePension) },
        ];
    } else {
        socialSecurityBreakdown = [{ label: 'Not eligible for Age Pension due to age.', value: '' }];
    }

    return { agePension, rentAssistanceIncome, totalAssessableIncome, socialSecurityBreakdown };
};
